import type { BridgeErrorCode, PageSessionPhase } from '@oysterjs/smartfill-contracts';
import { useSidebarSession } from '../bridge/session';
import { BucketBrowser } from '../components/BucketBrowser';
import { EntityPicker } from '../components/EntityPicker';
import { LearnedChanges } from '../components/LearnedChanges';
import { PageReviewCard } from '../components/PageReviewCard';
import { RetryButton, SidebarState } from '../components/SidebarState';
import { useBucket } from '../sidebar/data';
import { usePaste } from '../sidebar/paste';
import { useShowTechnicalProblems } from '../sidebar/preferences';
import { flattenDocumentProperties } from '../buckets/document';

const loadingPhases = new Set<PageSessionPhase>(['extracting', 'resolving', 'filling']);

export function ThisPageView() {
  const { snapshot } = useSidebarSession();
  const { tab, pageSession } = snapshot;
  const showTechnicalProblems = useShowTechnicalProblems();
  const activeBucket = useBucket(snapshot.activeBucket?.bucketId, true);
  const teachOptions = activeBucket.bucket
    ? flattenDocumentProperties(activeBucket.bucket.document)
    : [];
  const canShowFormData =
    tab.status === 'ready' &&
    !pageSession.error &&
    pageSession.phase !== 'error' &&
    !loadingPhases.has(pageSession.phase);
  const hasResult =
    canShowFormData && (pageSession.counts.total > 0 || pageSession.reviewRows.length > 0);
  const lowConfidenceRows = pageSession.reviewRows.filter((row) => row.state === 'REVIEW');
  const emptyRows = pageSession.reviewRows.filter((row) => row.state === 'EMPTY');
  const reviewSections = [
    { id: 'review', title: `${lowConfidenceRows.length} to review`, rows: lowConfidenceRows },
    { id: 'empty', title: `${emptyRows.length} empty to complete`, rows: emptyRows }
  ].filter((section) => section.rows.length > 0);

  return (
    <section className="page-view" aria-labelledby={hasResult ? 'page-fill-count' : undefined}>
      <PageStatus showTechnicalProblems={showTechnicalProblems} />
      {hasResult && (
        <>
          <EntityPicker rows={pageSession.reviewRows} document={activeBucket.bucket?.document} />
          {reviewSections.length === 0 ? (
            <SidebarState
              compact
              title="All fields reviewed"
              description="Cara has no flagged or empty values on this page."
            />
          ) : (
            reviewSections.map((section) => (
              <section key={section.id} aria-label={section.title}>
                <div className="section-heading section-heading--review">
                  <span className="review-dot" />
                  <h2>{section.title}</h2>
                </div>
                <div className="review-list">
                  {section.rows.map((row) => (
                    <PageReviewCard
                      key={`${pageSession.sessionId ?? tab.contextId}:${row.fieldId}`}
                      row={row}
                      tabContextId={tab.contextId}
                      teachOptions={teachOptions}
                      showTechnicalProblems={showTechnicalProblems}
                    />
                  ))}
                </div>
              </section>
            ))
          )}
        </>
      )}
      {snapshot.activeBucket && activeBucket.error && (
        <SidebarState
          compact
          kind="error"
          title="Active bucket could not load"
          description={activeBucket.error.message}
          action={
            activeBucket.error.retryable ? (
              <RetryButton onClick={activeBucket.reload}>Reload bucket</RetryButton>
            ) : undefined
          }
        />
      )}
      {activeBucket.bucket && (
        <LearnedChanges bucket={activeBucket.bucket} onReverted={activeBucket.setBucket} />
      )}
      {canShowFormData && activeBucket.bucket && <BucketBrowser bucket={activeBucket.bucket} />}
    </section>
  );
}

// Failures the user can resolve themselves; the rest read as one retry prompt
// unless technical problems are shown.
const ACTIONABLE_ERRORS = new Set<BridgeErrorCode>([
  'NOT_SIGNED_IN',
  'AUTH_EXPIRED',
  'NO_ACTIVE_TAB',
  'UNSUPPORTED_PAGE',
  'NO_ACTIVE_BUCKET',
  'NO_FORM_FOUND',
  // Continuous fill's session limit, which says how to grant a fresh budget.
  'SESSION_EXPIRED',
  'NETWORK'
]);

function PageStatus({ showTechnicalProblems }: { showTechnicalProblems: boolean }) {
  const { snapshot } = useSidebarSession();
  const paste = usePaste();
  const { tab, pageSession } = snapshot;

  if (tab.status === 'unavailable') {
    return (
      <SidebarState
        compact
        title="No active browser tab"
        description="Open a carrier form in a browser tab, then return to Smart Fill."
      />
    );
  }

  if (tab.status === 'unsupported') {
    return <SidebarState compact title="Smart Fill is unavailable on this page" />;
  }

  if (pageSession.error || pageSession.phase === 'error') {
    const error = pageSession.error;
    const named = error ? showTechnicalProblems || ACTIONABLE_ERRORS.has(error.code) : false;
    return (
      <SidebarState
        compact
        kind="error"
        title="This page could not be filled"
        description={
          error && named ? error.message : 'Cara could not finish filling this page. Try again.'
        }
        action={
          (error && named ? error.retryable : true) && snapshot.activeBucket ? (
            <RetryButton onClick={() => void paste.pasteOnce()}>Try filling again</RetryButton>
          ) : undefined
        }
      />
    );
  }

  if (loadingPhases.has(pageSession.phase)) {
    return (
      <SidebarState
        compact
        kind="loading"
        title={phaseTitle(pageSession.phase)}
        description="Cara is reading this page and matching fields from your active bucket."
      />
    );
  }

  if (pageSession.counts.total === 0 && pageSession.reviewRows.length === 0) {
    return (
      <SidebarState
        compact
        title={snapshot.activeBucket ? 'Ready to fill this page' : 'Choose a data bucket to begin'}
        description={
          snapshot.activeBucket
            ? 'Fill from the active data bucket to see fill counts and fields that need review.'
            : 'Data buckets keep one client’s fields together as the source for every fill.'
        }
      />
    );
  }

  return (
    <header className="page-fill-summary">
      <div className="fill-count-row" id="page-fill-count">
        <strong>
          {pageSession.counts.filled} {pageSession.counts.filled === 1 ? 'field' : 'fields'} filled
        </strong>
      </div>
      {pageSession.outcome && pageSession.outcome.kind !== 'SUCCESS' && (
        <p className="page-outcome" role="status">
          {pageSession.outcome.summary}
        </p>
      )}
    </header>
  );
}

function phaseTitle(phase: PageSessionPhase): string {
  switch (phase) {
    case 'extracting':
      return 'Reading this page';
    case 'resolving':
      return 'Matching fields';
    case 'filling':
      return 'Filling this page';
    default:
      return 'Preparing Smart Fill';
  }
}
